import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../hooks/useAuth'
import { Suspense, useState } from 'react'
import { Home, Star, PawPrint, ShoppingBag, GraduationCap, LogOut, UserCircle, ShoppingCart, Bot, MessageCircle, Package, X, Library } from 'lucide-react'

// Ícono personalizado para Eco Canje (usa la imagen de Pulpín)
const EcoIcon = ({ size = 20 }: { size?: number }) => (
  <img src="/eco-canje.png" alt="Eco" style={{ width: size, height: size }} className="object-contain" />
)
import PulpinWidget from '../ui/PulpinWidget'
import InstallBanner from '../ui/InstallBanner'
import clsx from 'clsx'
import { useCartStore } from '../../store/cartStore'
import { useMe } from '../../hooks/useMe'

// Ícono Pulpín PNG para el nav del juego
function PulpinNavIcon({ size }: { size: number }) {
  return (
    <img src="/game/pulpin/pulpin.png" alt="Juego"
      style={{ width: size, height: size }}
      className="object-contain"
      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
    />
  )
}

function ContentLoader() {
  return (
    <div className="flex-1 flex items-center justify-center py-24">
      <div className="w-8 h-8 border-4 border-aqua-200 border-t-aqua-500 rounded-full animate-spin" />
    </div>
  )
}

const navItems = [
  { label: 'Inicio',    to: '/cliente',           icon: Home,           end: true },
  { label: 'Juego',     to: '/cliente/juego',     icon: PulpinNavIcon },
  { label: 'Catálogo',  to: '/cliente/catalogo',  icon: ShoppingBag },
  { label: 'Puntos',    to: '/cliente/puntos',    icon: Star },
  { label: 'Mascotas',  to: '/cliente/mascotas',  icon: PawPrint },
  { label: 'Perfil',    to: '/cliente/perfil',    icon: UserCircle },
]

const navItemsExtra: { label: string; to: string; icon: any; end?: boolean }[] = [
  { label: 'AquaLearn', to: '/cliente/diario',    icon: GraduationCap },
  { label: 'Biblioteca', to: '/cliente/biblioteca', icon: Library },
  { label: 'Pulpín',    to: '/cliente/pulpin',    icon: Bot },
  { label: 'Mensajes',  to: '/cliente/mensajes',  icon: MessageCircle },
  { label: 'Pedidos',   to: '/cliente/pedidos',   icon: Package },
  { label: 'Eco',       to: '/cliente/eco',        icon: EcoIcon },
]

export default function ClienteLayout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const cartCount = useCartStore(s => s.totalItems())
  const { pathname } = useLocation()
  const isGame = pathname === '/cliente/juego'
  const { data: me } = useMe()
  const puntos = me?.clientePerfil?.puntos ?? null
  const [ocultarCarrito, setOcultarCarrito] = useState(false)
  const mostrarRecordatorioCarrito = cartCount > 0 && !isGame && !ocultarCarrito && !pathname.includes('checkout')

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">

      {/* ── Sidebar desktop (lg+) ───────────────────────────────────────────── */}
      <aside className="hidden lg:flex flex-col w-56 bg-white border-r border-gray-100 fixed inset-y-0 left-0 z-30">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-gray-100">
          <img src="/logo-aqua.png" alt="Aqua" className="h-8 w-auto" />
        </div>

        {/* Nav principal */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {[...navItems, ...navItemsExtra].map(({ label, to, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all',
                isActive
                  ? 'bg-aqua-500 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
              )}>
              <Icon size={17} />{label}
            </NavLink>
          ))}
        </nav>

        {/* Usuario + logout */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-aqua-100 flex items-center justify-center text-aqua-600 font-bold text-sm flex-shrink-0 overflow-hidden">
              {me?.avatar
                ? <img src={me.avatar} alt="Perfil" className="w-full h-full object-cover" />
                : (user?.nombre?.slice(0, 2).toUpperCase() ?? '?')
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-gray-800 truncate">{user?.nombre}</div>
              <div className="text-xs text-gray-400">Cliente</div>
            </div>
            <button onClick={logout} title="Cerrar sesión"
              className="text-gray-300 hover:text-red-400 p-1 transition-colors">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Contenido principal ─────────────────────────────────────────────── */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-screen">

        {/* Header sticky */}
        <header className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 h-14 flex items-center justify-between">
          <img src="/logo-aqua.png" alt="Aqua" className="h-7 w-auto lg:hidden" />

          <div className="flex items-center gap-2">
            {/* Puntos Aqua — se actualiza en tiempo real */}
            <NavLink to="/cliente/puntos"
              className="hidden sm:flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 hover:bg-amber-100 px-2.5 py-1 rounded-full transition-colors">
              ⭐ {puntos !== null ? puntos.toLocaleString('es-CO') : '—'}
            </NavLink>

            {/* Carrito */}
            {cartCount > 0 && (
              <button onClick={() => navigate('/cliente/checkout')}
                className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ShoppingCart size={20} className="text-gray-600" />
                <span className="absolute -top-0.5 -right-0.5 bg-aqua-500 text-white text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              </button>
            )}

            {/* Avatar móvil → perfil */}
            <NavLink to="/cliente/perfil"
              className="lg:hidden w-8 h-8 rounded-full bg-aqua-100 flex items-center justify-center text-aqua-600 font-bold text-xs overflow-hidden flex-shrink-0">
              {me?.avatar
                ? <img src={me.avatar} alt="Perfil" className="w-full h-full object-cover" />
                : (user?.nombre?.slice(0, 2).toUpperCase() ?? '?')
              }
            </NavLink>

            {/* Logout móvil */}
            <button onClick={logout} title="Cerrar sesión"
              className="lg:hidden p-2 rounded-xl hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Recordatorio de carrito pendiente (no invasivo: solo si vuelve a la app) */}
        {mostrarRecordatorioCarrito && (
          <div className="px-4 pt-3">
            <div className="flex items-center gap-3 bg-aqua-50 border border-aqua-200 rounded-xl px-4 py-2.5">
              <ShoppingCart size={18} className="text-aqua-600 flex-shrink-0" />
              <span className="text-sm text-aqua-800 flex-1 leading-snug">
                Tienes <strong>{cartCount}</strong> producto{cartCount > 1 ? 's' : ''} esperándote en tu carrito. ¿Terminamos tu compra?
              </span>
              <button onClick={() => navigate('/cliente/checkout')}
                className="text-xs font-bold bg-aqua-500 text-white px-3 py-1.5 rounded-lg hover:bg-aqua-600 transition-colors whitespace-nowrap">
                Ir al carrito
              </button>
              <button onClick={() => setOcultarCarrito(true)} className="text-aqua-400 hover:text-aqua-600 p-1" aria-label="Cerrar">
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Página — sin padding cuando el juego está activo (pantalla completa) */}
        <main className={isGame ? 'flex-1 p-0 w-full overflow-hidden' : 'flex-1 px-4 py-5 w-full pb-24 lg:pb-6'}>
          <Suspense fallback={<ContentLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* ── Bottom nav móvil — oculto durante el juego para pantalla completa ── */}
      <nav className={`lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-100 ${isGame ? 'hidden' : ''}`}>
        <div className="flex items-stretch h-16 overflow-x-auto scrollbar-none">
          {[...navItems, ...navItemsExtra].map(({ label, to, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => clsx(
                'flex-shrink-0 flex flex-col items-center justify-center gap-0.5 px-3 transition-colors min-w-[64px]',
                isActive ? 'text-aqua-500' : 'text-gray-400'
              )}>
              {({ isActive }) => (
                <>
                  <div className={clsx('p-1 rounded-xl transition-all', isActive && 'bg-aqua-50')}>
                    <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                  </div>
                  <span className="text-[10px] font-semibold">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Widget Pulpín — oculto en el juego */}
      {!isGame && <PulpinWidget />}

      {/* Banner PWA */}
      <InstallBanner />
    </div>
  )
}
